import Confirm from '@hobs/components/Confirm';
import React from 'react';
import { Prompt } from 'react-router-dom';

const withNavigationPrompt = (WrappedComponent, options = {}) => {
  const dialogMessage =
    options.dialogMessage ||
    'You have unsaved changes. Are you sure you want to leave?';

  class WithNavigationPrompt extends React.PureComponent {
    constructor(props) {
      super(props);
      this.state = {
        isBlocking: false,
        showDialog: false,
        pendingLocation: null
      };
    }

    // Method to be called by child component to set blocking state
    setNavigationBlocking = isBlocking => {
      this.setState({ isBlocking });
    };

    // Method to be called by child component before navigation
    // Returns a promise that resolves when navigation is allowed
    confirmNavigation = async asyncCallback => {
      // If we're not blocking, resolve immediately
      if (!this.state.isBlocking) {
        return Promise.resolve(true);
      }

      // Return a promise that resolves when confirmed
      return new Promise(resolve => {
        this.resolveNavigation = resolve;

        // Store the async callback if provided
        this.asyncCallback = asyncCallback;

        // Show the dialog
        this.setState({ showDialog: true });
      });
    };

    // Handle blocked navigation
    handleBlockedNavigation = location => {
      const isSwitchingExercise =
        location.pathname === '/' && location.state === undefined;
      // in sor requisites page, allow navigation to activity modal and back without prompt
      if (
        location.pathname.includes('/sor-requisites') ||
        isSwitchingExercise
      ) {
        return true;
      }

      if (this.state.isBlocking) {
        // Store the location the user was trying to navigate to
        this.setState({
          pendingLocation: location,
          showDialog: true
        });

        // Return false to prevent immediate navigation
        return false;
      }
      return true;
    };

    // Handle dialog confirmation
    handleConfirm = async () => {
      try {
        // Run the async callback if provided
        if (this.asyncCallback && typeof this.asyncCallback === 'function') {
          await this.asyncCallback();
        }

        // Complete the navigation
        this.setState(
          {
            isBlocking: false,
            showDialog: false
          },
          () => {
            // Resolve the navigation promise if it exists
            if (this.resolveNavigation) {
              this.resolveNavigation(true);
              this.resolveNavigation = null;
            }

            // If there's a pending location, navigate programmatically
            if (this.state.pendingLocation && this.props.history) {
              this.props.history.push(this.state.pendingLocation.pathname);
              this.setState({ pendingLocation: null });
            }
          }
        );
      } catch (error) {
        console.error('Navigation async operation failed:', error);
        this.setState({ showDialog: false });

        // Reject the navigation promise if it exists
        if (this.resolveNavigation) {
          this.resolveNavigation(false);
          this.resolveNavigation = null;
        }
      }
    };

    // Handle dialog cancellation
    handleCancel = () => {
      this.setState({
        showDialog: false,
        pendingLocation: null
      });

      // Resolve the navigation promise with false if it exists
      if (this.resolveNavigation) {
        this.resolveNavigation(false);
        this.resolveNavigation = null;
      }
    };

    render() {
      const { isBlocking, showDialog } = this.state;

      return (
        <>
          <Prompt when={isBlocking} message={this.handleBlockedNavigation} />

          {showDialog &&
            Confirm(
              'Attention',
              dialogMessage,
              () => {
                this.handleConfirm();
              },
              () => {
                this.handleCancel();
              },
              'Yes',
              'No'
            )}

          <WrappedComponent
            {...this.props}
            setNavigationBlocking={this.setNavigationBlocking}
            confirmNavigation={this.confirmNavigation}
          />
        </>
      );
    }
  }

  return WithNavigationPrompt;
};

export default withNavigationPrompt;
